import { useEffect, useState } from "react";
import { Save, Wallet, Plus, Trash2, GripVertical, Zap } from "lucide-react";
import PageHeader from "../components/PageHeader";
import ConfigTabs from "../components/ConfigTabs";
import useSettings from "../hooks/useSettings";
import api from "../lib/api";

const BLANK_METHOD = {
  id: "",
  name: "",
  enabled: true,
  min: 500,
  max: 25000,
  fee_pct: 0,
  account_label: "Account Number",
  processing_hours: 24,
};

export default function ConfigWithdraw() {
  const { form, set, save, loading } = useSettings();
  const [saving, setSaving] = useState(false);
  const [gateways, setGateways] = useState([]);

  useEffect(() => {
    api.get("/site/admin/gateways").then(({ data }) => {
      setGateways(data.items || []);
    }).catch(() => setGateways([]));
  }, []);

  if (!form) return <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading...</div>;

  const methods = Array.isArray(form.withdraw_methods) ? form.withdraw_methods : [];

  const updateMethod = (idx, key, value) => {
    const next = methods.slice();
    next[idx] = { ...next[idx], [key]: value };
    set("withdraw_methods", next);
  };

  const addMethod = () => {
    set("withdraw_methods", [...methods, { ...BLANK_METHOD, id: `method-${Date.now()}`, name: "New method" }]);
  };

  const removeMethod = (idx) => {
    const next = methods.slice();
    next.splice(idx, 1);
    set("withdraw_methods", next);
  };

  const onSave = async () => {
    setSaving(true);
    await save();
    setSaving(false);
  };

  return (
    <div data-testid="config-withdraw-page">
      <PageHeader title="Configuration" subtitle="Withdraw Page · control methods, limits and flow shown on the player site" />
      <ConfigTabs />

      <div className="space-y-5 max-w-5xl">
        {/* Global toggles */}
        <Section title="Withdraw Page Controls" icon={Wallet}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Toggle
              label="Withdrawals enabled"
              hint="Master kill switch shown to players on /profile/withdraw"
              checked={!!form.withdraw_enabled}
              onChange={(v) => set("withdraw_enabled", v)}
              testid="cfg-wd-enabled"
            />
            <Toggle
              label="Instant debit on request"
              hint="When OFF (recommended), balance is debited only when admin approves"
              checked={!!form.withdraw_instant_debit}
              onChange={(v) => set("withdraw_instant_debit", v)}
              testid="cfg-wd-instant"
            />
          </div>
          <div className="mt-4">
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Notice shown on withdraw page</label>
            <textarea
              rows={2}
              value={form.withdraw_notice || ""}
              onChange={(e) => set("withdraw_notice", e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm"
              placeholder="Withdrawals are processed within 24 hours."
              data-testid="cfg-wd-notice"
            />
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Global Min Withdraw (fallback)">
              <input type="number" step="0.01" value={form.min_withdraw ?? 0} onChange={(e) => set("min_withdraw", parseFloat(e.target.value) || 0)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" />
            </Field>
            <Field label="Global Max Withdraw (fallback)">
              <input type="number" step="0.01" value={form.max_withdraw ?? 0} onChange={(e) => set("max_withdraw", parseFloat(e.target.value) || 0)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" />
            </Field>
            <Field label="Max Withdrawals / Day">
              <input type="number" value={form.max_withdrawals_per_day ?? 0} onChange={(e) => set("max_withdrawals_per_day", parseInt(e.target.value) || 0)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" />
            </Field>
          </div>
        </Section>

        {/* Auto-payout settings */}
        <Section title="Auto-Payout (Small Withdrawals)" icon={Zap}>
          <div className="text-xs text-gray-600 mb-4 bg-amber-50 border border-amber-200 rounded-sm p-3">
            <div className="font-semibold text-amber-900 mb-1">⚡ How it works</div>
            When a player submits a withdrawal at or below the <b>threshold amount</b> using one of the
            <b> enabled methods</b>, the system will immediately submit a payout to the selected gateway
            and auto-approve the request when the gateway confirms — no admin review needed.
            Larger amounts still follow the normal admin-approval flow.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <Toggle
              label="Auto-payout enabled"
              hint="Master toggle. Turn OFF to force every withdrawal through admin approval."
              checked={!!form.auto_withdraw_enabled}
              onChange={(v) => set("auto_withdraw_enabled", v)}
              testid="cfg-autopay-enabled"
            />
            <div />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Threshold amount (BDT)">
              <input
                type="number"
                step="1"
                min="0"
                value={form.auto_withdraw_max_amount ?? 0}
                onChange={(e) => set("auto_withdraw_max_amount", parseFloat(e.target.value) || 0)}
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular"
                placeholder="e.g. 5000"
                data-testid="cfg-autopay-threshold"
              />
              <div className="text-[11px] text-gray-500 mt-1">
                Withdrawals ≤ this amount will auto-process. Set to <b>0</b> to disable.
              </div>
            </Field>
            <Field label="Payout gateway">
              <select
                value={form.auto_withdraw_gateway_id ?? ""}
                onChange={(e) => set("auto_withdraw_gateway_id", e.target.value)}
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm bg-white"
                data-testid="cfg-autopay-gateway"
              >
                <option value="">— Select gateway —</option>
                {gateways.filter((g) => g.enabled && ["hrpay", "cowpay", "starpago", "wpay", "tarspay"].includes(g.provider)).map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.label} ({g.provider})
                  </option>
                ))}
              </select>
              <div className="text-[11px] text-gray-500 mt-1">
                Only enabled HRpay / Cowpay / StarPago / WPay / TarsPay gateways support auto-payout.
              </div>
            </Field>
          </div>
          <div className="mt-4">
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              Auto-payout methods (what players can choose for small withdrawals)
            </label>
            <div className="flex flex-wrap gap-2">
              {["bkash", "nagad", "rocket", "upay"].map((m) => {
                const active = (form.auto_withdraw_methods || []).includes(m);
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => {
                      const cur = Array.isArray(form.auto_withdraw_methods) ? form.auto_withdraw_methods : [];
                      const next = active ? cur.filter((x) => x !== m) : [...cur, m];
                      set("auto_withdraw_methods", next);
                    }}
                    className={`h-9 px-3.5 rounded-sm text-xs font-semibold uppercase tracking-wide border transition ${
                      active
                        ? "bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600"
                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                    }`}
                    data-testid={`cfg-autopay-method-${m}`}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </div>
        </Section>

        {/* Methods */}
        <div className="bg-white border border-gray-200 rounded-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Wallet size={14} className="text-emerald-500" />
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Withdraw Methods</div>
            </div>
            <button
              type="button"
              onClick={addMethod}
              className="h-9 px-3 bg-gray-900 hover:bg-gray-800 text-white rounded-sm text-xs font-semibold inline-flex items-center gap-1.5"
              data-testid="cfg-wd-add-method"
            >
              <Plus size={14} /> Add method
            </button>
          </div>
          <div className="p-5 space-y-4">
            {methods.length === 0 && (
              <div className="text-sm text-gray-500 text-center py-6 border border-dashed border-gray-200 rounded-sm">
                No methods configured. Click "Add method" to expose one on the site withdraw page.
              </div>
            )}
            {methods.map((m, idx) => (
              <div key={idx} className="border border-gray-200 rounded-sm p-4" data-testid={`cfg-wd-method-${idx}`}>
                <div className="flex items-center gap-3 mb-3">
                  <GripVertical size={14} className="text-gray-300" />
                  <input
                    value={m.name || ""}
                    onChange={(e) => updateMethod(idx, "name", e.target.value)}
                    placeholder="Method display name"
                    className="flex-1 h-10 px-3 border border-gray-200 rounded-sm text-sm font-semibold"
                    data-testid={`cfg-wd-name-${idx}`}
                  />
                  <label className="inline-flex items-center gap-2 text-xs font-semibold">
                    <input
                      type="checkbox"
                      checked={!!m.enabled}
                      onChange={(e) => updateMethod(idx, "enabled", e.target.checked)}
                      data-testid={`cfg-wd-enabled-${idx}`}
                    />
                    <span className={m.enabled ? "text-emerald-600" : "text-gray-400"}>
                      {m.enabled ? "Enabled" : "Disabled"}
                    </span>
                  </label>
                  <button type="button" onClick={() => removeMethod(idx)} className="p-2 rounded-sm hover:bg-rose-50 text-rose-500" data-testid={`cfg-wd-delete-${idx}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                  <Field label="ID (slug)">
                    <input value={m.id || ""} onChange={(e) => updateMethod(idx, "id", e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" placeholder="bkash" />
                  </Field>
                  <Field label="Min (৳)">
                    <input type="number" step="0.01" value={m.min ?? 0} onChange={(e) => updateMethod(idx, "min", parseFloat(e.target.value) || 0)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" />
                  </Field>
                  <Field label="Max (৳)">
                    <input type="number" step="0.01" value={m.max ?? 0} onChange={(e) => updateMethod(idx, "max", parseFloat(e.target.value) || 0)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" />
                  </Field>
                  <Field label="Fee %">
                    <input type="number" step="0.01" value={m.fee_pct ?? 0} onChange={(e) => updateMethod(idx, "fee_pct", parseFloat(e.target.value) || 0)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" />
                  </Field>
                  <Field label="Processing hrs">
                    <input type="number" value={m.processing_hours ?? 24} onChange={(e) => updateMethod(idx, "processing_hours", parseInt(e.target.value) || 0)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" />
                  </Field>
                  <Field label="Account field label">
                    <input value={m.account_label || ""} onChange={(e) => updateMethod(idx, "account_label", e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" placeholder="Account Number" />
                  </Field>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onSave}
            disabled={loading || saving}
            className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="cfg-wd-save"
          >
            <Save size={14} /> {saving || loading ? "Saving..." : "Save Withdraw Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-5">
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
        {Icon && <Icon size={14} className="text-emerald-500" />}
        <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">{title}</div>
      </div>
      {children}
    </div>
  );
}

function Toggle({ label, hint, checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="mt-0.5" />
      <div>
        <div className="text-sm font-semibold text-gray-900">{label}</div>
        {hint && <div className="text-xs text-gray-500 mt-0.5">{hint}</div>}
      </div>
    </label>
  );
}
